import type { ReactNode } from 'react';
import { cn } from '@/shared/lib';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

/**
 * The tone of a row action.
 *
 * EVERY TONE IS OUTLINED. None of them is filled.
 *
 * A filled button in every row of a table is a wall of colour: the fill stops
 * meaning "this one is the action" and starts meaning "this table is a control
 * surface". An outline carries the same colour at a fraction of the weight, so
 * a row of actions reads as a row of affordances rather than as a row of
 * buttons competing with the data beside them.
 *
 * The colour still separates the tones: `primary` is the accent purple,
 * `danger` the critical red, `warning` the caution amber, and `neutral` an
 * uncoloured border. The fill only appears on hover, where it confirms the
 * pointer is on the control.
 */
export type RowActionTone = 'primary' | 'neutral' | 'danger' | 'warning';

export type RowActionButtonProps = {
  /** The action's name. Shown in the tooltip and used as the accessible name. */
  label: string;
  /** The icon. Decorative — the label carries the meaning. */
  icon: ReactNode;
  /** Visual weight. Defaults to `primary`. */
  tone?: RowActionTone;
  /** Called when the action is activated. */
  onClick?: () => void;
  /** Blocks activation and dims the control. */
  disabled?: boolean;
  /**
   * Why the action is unavailable.
   *
   * When set, the tooltip shows this INSTEAD of the label, and the control is
   * disabled with `aria-disabled` rather than the `disabled` attribute.
   *
   * The `disabled` attribute would suppress the pointer events the tooltip
   * needs, so a disabled action would be a dead square with no way to learn why
   * it is dead. `aria-disabled` keeps the control focusable and hoverable — the
   * tooltip opens and states the reason — while `onClick` is short-circuited
   * here so the action still cannot fire.
   */
  disabledReason?: string;
};

/**
 * One icon-only action in a table row.
 *
 * ICON ONLY, WITH THE LABEL IN A TOOLTIP.
 * A row of text buttons ("View", "Edit", "Delete") makes the action column the
 * widest thing in the table and forces every row to be as tall as a button.
 * An icon button is a fixed square, so the column is narrow and the row height
 * is set by the data rather than by the controls.
 *
 * The label is NOT lost: it is the button's accessible name (`aria-label`) and
 * the tooltip's text. The tooltip opens on hover AND on keyboard focus, so the
 * name is reachable without a pointer — an icon button whose only label is a
 * hover tooltip is unusable by keyboard and by touch.
 *
 * The tooltip is supplementary, never the sole carrier of meaning: the
 * `aria-label` is what assistive technology announces, and it is present
 * whether or not the tooltip is open.
 *
 * The tooltip is portaled to `<body>` and its positioning layer carries a
 * z-index, so it clears the sticky cells of the table it sits in rather than
 * rendering behind the pinned column.
 */
export function RowActionButton({
  label,
  icon,
  tone = 'primary',
  onClick,
  disabled = false,
  disabledReason,
}: RowActionButtonProps) {
  // A reason makes the control "soft-disabled": it stays hoverable and
  // focusable so the tooltip can explain itself, and the click is swallowed
  // here instead of by the `disabled` attribute.
  const blocked = disabled || disabledReason !== undefined;
  const softDisabled = disabledReason !== undefined;

  return (
    <Tooltip>
      <TooltipTrigger
        aria-label={label}
        {...(softDisabled ? { 'aria-disabled': true } : { disabled })}
        onClick={blocked ? undefined : onClick}
        className={cn(
          dataTableStyles.rowAction,
          dataTableStyles.rowActionTone[tone],
          blocked && dataTableStyles.rowActionDisabled,
        )}
      >
        {icon}
      </TooltipTrigger>
      <TooltipContent side="top">{disabledReason ?? label}</TooltipContent>
    </Tooltip>
  );
}
